import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import Input from "components/ui/Input";
import authHeader from "services/auth-header";
import { getDocumentUrls } from "utils/documentUrlHelper";
import useDocumentActions from "hooks/useDocumentActions";
import useFullScreenLayout from "hooks/useFullScreenLayout";
import {
  EmailIcon,
  WhatsAppIcon,
  DownloadIcon,
  PrintIcon,
} from "components/ui/icons/DocumentIcons";

/**
 * Document Viewer Page
 * Displays document HTML in iframe (left 3/4) and action panel (right 1/4)
 * Responsive: stacks vertically on mobile
 * 
 * Supports on-the-fly report generation via query params:
 *   generate=true&apiEndpoint=/salebill/agent-sales-report&apiBody={...json...}
 * When present, calls the API first, then displays the result.
 */
export default function DocumentViewer() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shouldGenerate = searchParams.get("generate") === "true";
  const apiEndpoint = searchParams.get("apiEndpoint");
  const apiBody = searchParams.get("apiBody");

  const [generatedHtmlUrl, setGeneratedHtmlUrl] = useState(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(shouldGenerate);
  const [generateError, setGenerateError] = useState(null);

  const htmlUrlParam = searchParams.get("htmlUrl");
  const htmlUrl = shouldGenerate ? generatedHtmlUrl : htmlUrlParam;
  const pdfUrl = shouldGenerate ? generatedPdfUrl : searchParams.get("pdfUrl");
  const documentNumber = searchParams.get("number");
  const documentType = searchParams.get("type") || "Document";

  useFullScreenLayout();

  useEffect(() => {
    if (!shouldGenerate || !apiEndpoint || !apiBody) return;

    const generateReport = async () => {
      setIsGenerating(true);
      setGenerateError(null);
      try {
        const body = JSON.parse(apiBody);
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}${apiEndpoint}`,
          body,
          { headers: authHeader() }
        );
        const data = res.data.data;
        const { htmlUrl: fullHtmlUrl, pdfUrl: fullPdfUrl } = getDocumentUrls(data);
        setGeneratedHtmlUrl(fullHtmlUrl);
        setGeneratedPdfUrl(fullPdfUrl);
      } catch (err) {
        console.error("Error generating report:", err);
        setGenerateError(err?.response?.data?.msg || "Error generating report");
      } finally {
        setIsGenerating(false);
      }
    };

    generateReport();
  }, [shouldGenerate, apiEndpoint, apiBody]);

  // Action panel visibility (sidebar) — closed by default
  const [showActionsPanel, setShowActionsPanel] = useState(false);

  // Form data for sending communications
  const [emailRecipients, setEmailRecipients] = useState({
    vendorGeneral: false,
    vendorSales: false,
    vendorAccounts: false,
    vendorScheme: false,
    customerGeneral: false,
    customerSales: false,
    customerAccounts: false,
    customerScheme: false,
  });

  const {
    handleSendEmail,
    handleSendWhatsApp,
    handleDownloadPdf,
    handlePrint,
    isLoading,
  } = useDocumentActions({
    documentType,
    documentNumber,
    pdfUrl,
  });

  // Toggle email recipient
  const toggleEmailRecipient = (recipient) => {
    setEmailRecipients((prev) => ({
      ...prev,
      [recipient]: !prev[recipient],
    }));
  };

  // WhatsApp handler - each button sends to one specific contact type
  const handleWhatsAppClick = (recipientType) => {
    const whatsappData = {
      documentId: id,
      documentType,
      documentNumber,
      pdfUrl,
      htmlUrl,
      recipients: {
        [recipientType]: true, // Only one recipient type at a time
      },
    };
    handleSendWhatsApp(whatsappData);
  };

  // Handle send email button click - calls API with document info
  const onSendEmail = () => {
    const emailData = {
      documentId: id,
      documentType,
      documentNumber,
      pdfUrl,
      htmlUrl,
      recipients: emailRecipients,
    };
    handleSendEmail(emailData);
  };

  // Handle send WhatsApp - calls API with document info

  if (isGenerating) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Generating report...</p>
        </div>
      </div>
    );
  }

  if (generateError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 font-semibold">{generateError}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!htmlUrl) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 font-semibold">Missing document URL</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col -my-10 h-[calc(100dvh-4rem)] bg-slate-100">
      {/* Back Button Bar */}
      <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4 bg-white border-b border-gray-200 shadow-sm shrink-0 z-20">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 shrink-0"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        {documentNumber && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-800">
              {documentNumber}
            </p>
            <p className="hidden sm:block truncate text-xs text-gray-500">
              {documentType}
            </p>
          </div>
        )}
        {!documentNumber && <div className="flex-1" />}

        {/* Mobile: toggle actions drawer */}
        <button
          onClick={() => setShowActionsPanel((prev) => !prev)}
          className="ml-auto lg:hidden inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-gray-800 shrink-0"
          title={showActionsPanel ? "Hide actions" : "Show actions"}
        >
          <DocumentTextIcon className="h-4 w-4" />
          Actions
          {showActionsPanel ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="relative flex flex-col lg:flex-row flex-1 min-h-0">
      {/* Document iframe */}
      <div className="flex-1 min-h-0 overflow-hidden bg-slate-100 p-1.5 sm:p-2 lg:p-3 relative">
        <div className="h-full overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
          <iframe
            src={htmlUrl}
            title="Document Preview"
            className="origin-top-left h-[227.28%] w-[227.28%] scale-[.44] border-0 sm:h-[142.86%] sm:w-[142.86%] sm:scale-[.7] md:h-[125%] md:w-[125%] md:scale-[.8] lg:h-full lg:w-full lg:scale-100"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
        
        {/* Desktop Toggle Button */}
        {!showActionsPanel && (
          <button
            onClick={() => setShowActionsPanel(true)}
            className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 items-center justify-center bg-gray-700 text-white rounded-full shadow-md hover:bg-gray-800 z-20"
            title="Show Actions Panel"
          >
            <ChevronUpIcon className="w-3 h-3 rotate-90" />
          </button>
        )}
      </div>

      {/* Action Panel / Sidebar */}
      {showActionsPanel && (
        <>
          <button
            type="button"
            aria-label="Close actions"
            onClick={() => setShowActionsPanel(false)}
            className="absolute inset-0 z-20 bg-slate-950/20 lg:hidden"
          />
          <div className="absolute inset-x-0 bottom-0 z-30 max-h-[72%] rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 lg:static lg:z-auto lg:h-full lg:w-[22rem] lg:max-h-none lg:rounded-none lg:border-l lg:border-gray-200 lg:shadow-none shrink-0">
        {/* Desktop Hide Button */}
        <button
          onClick={() => setShowActionsPanel(false)}
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 items-center justify-center bg-gray-700 text-white rounded-full shadow-md hover:bg-gray-800 z-20"
          title="Hide Actions Panel"
        >
          <ChevronUpIcon className="w-3 h-3 -rotate-90" />
        </button>
        
        <div className="h-full overflow-y-auto">
        <div className="p-4 lg:p-4">
            <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3 lg:hidden">
              <div>
                <p className="text-sm font-semibold text-gray-900">Document actions</p>
                <p className="text-xs text-gray-500">Download, print, or share this document</p>
              </div>
              <button
                type="button"
                onClick={() => setShowActionsPanel(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close actions"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 mb-5 lg:mb-6">
              <button
                onClick={handleDownloadPdf}
                disabled={!pdfUrl}
                className="flex items-center justify-center gap-1.5 px-2.5 py-2.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download PDF"
              >
                <DownloadIcon className="w-4 h-4" />
                <span>Download</span>
              </button>
              <button
                onClick={handlePrint}
                disabled={!pdfUrl}
                className="flex items-center justify-center gap-1.5 px-2.5 py-2.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Print"
              >
                <PrintIcon className="w-4 h-4" />
                <span>Print</span>
              </button>
            </div>

            {/* Email Section */}
            <div className="mb-4 lg:mb-6">
              <div className="flex items-center gap-2 mb-2 lg:mb-3">
                <EmailIcon className="w-4 lg:w-5 h-4 lg:h-5 text-green-600" />
                <h3 className="text-xs lg:text-sm font-semibold text-gray-900">
                  Email Recipients
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5 lg:mb-2">Vendor</p>
                  <div className="space-y-1 lg:space-y-1.5">
                    <Input
                      type="checkbox"
                      label="General"
                      value={emailRecipients.vendorGeneral}
                      onChange={() => toggleEmailRecipient("vendorGeneral")}
                    />
                    <Input
                      type="checkbox"
                      label="Sales"
                      value={emailRecipients.vendorSales}
                      onChange={() => toggleEmailRecipient("vendorSales")}
                    />
                    <Input
                      type="checkbox"
                      label="Accounts"
                      value={emailRecipients.vendorAccounts}
                      onChange={() => toggleEmailRecipient("vendorAccounts")}
                    />
                    <Input
                      type="checkbox"
                      label="Scheme"
                      value={emailRecipients.vendorScheme}
                      onChange={() => toggleEmailRecipient("vendorScheme")}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5 lg:mb-2">Customer</p>
                  <div className="space-y-1 lg:space-y-1.5">
                    <Input
                      type="checkbox"
                      label="General"
                      value={emailRecipients.customerGeneral}
                      onChange={() => toggleEmailRecipient("customerGeneral")}
                    />
                    <Input
                      type="checkbox"
                      label="Sales"
                      value={emailRecipients.customerSales}
                      onChange={() => toggleEmailRecipient("customerSales")}
                    />
                    <Input
                      type="checkbox"
                      label="Accounts"
                      value={emailRecipients.customerAccounts}
                      onChange={() => toggleEmailRecipient("customerAccounts")}
                    />
                    <Input
                      type="checkbox"
                      label="Scheme"
                      value={emailRecipients.customerScheme}
                      onChange={() => toggleEmailRecipient("customerScheme")}
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={onSendEmail}
                disabled={isLoading}
                className="w-full mt-3 lg:mt-4 flex items-center justify-center gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs lg:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EmailIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4" />
                Send Email
              </button>
            </div>

            {/* WhatsApp Section */}
            <div>
              <div className="flex items-center gap-2 mb-2 lg:mb-3">
                <WhatsAppIcon className="w-4 lg:w-5 h-4 lg:h-5 text-green-500" />
                <h3 className="text-xs lg:text-sm font-semibold text-gray-900">
                  WhatsApp
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5 lg:mb-2">Vendor</p>
                  <div className="space-y-1 lg:space-y-1.5">
                    <button
                      onClick={() => handleWhatsAppClick("vendorGeneral")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">General</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("vendorSales")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Sales</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("vendorAccounts")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Accounts</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("vendorScheme")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Scheme</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5 lg:mb-2">Customer</p>
                  <div className="space-y-1 lg:space-y-1.5">
                    <button
                      onClick={() => handleWhatsAppClick("customerGeneral")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">General</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("customerSales")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Sales</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("customerAccounts")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Accounts</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => handleWhatsAppClick("customerScheme")}
                      disabled={isLoading}
                      className="w-full flex items-center justify-between px-2 lg:px-2.5 py-1.5 lg:py-2 text-xs lg:text-sm text-left border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-gray-700 truncate">Scheme</span>
                      <WhatsAppIcon className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-green-500 flex-shrink-0" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
        </>
      )}
      </div>
    </div>
  );
}

